// One primary button per screen. Everything else is secondary or ghost.
// Pass `to` for in-app navigation (renders a router Link), otherwise it is a <button>.
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cx } from '@/lib/cx';
import { Icon, type IconName } from './Icon';

type ButtonProps = {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  /** Icon before the label. */
  icon?: IconName;
  /** Icon after the label, e.g. arrow-right on "Start". */
  iconEnd?: IconName;
  to?: string;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  className?: string;
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  block,
  icon,
  iconEnd,
  to,
  onClick,
  type = 'button',
  disabled,
  className,
}: ButtonProps) {
  const cls = cx('btn', variant !== 'primary' && variant, size !== 'md' && size, block && 'block', className);
  const content = (
    <>
      {icon && <Icon name={icon} />}
      {children}
      {iconEnd && <Icon name={iconEnd} />}
    </>
  );
  if (to && !disabled) {
    return (
      <Link className={cls} to={to} onClick={onClick}>
        {content}
      </Link>
    );
  }
  return (
    <button className={cls} type={type} onClick={onClick} disabled={disabled}>
      {content}
    </button>
  );
}

type IconButtonProps = {
  icon: IconName;
  /** Required: what the button does, for screen readers. */
  label: string;
  to?: string;
  onClick?: () => void;
  className?: string;
};

/** Round 44px button with only an icon (settings, close, play a word). */
export function IconButton({ icon, label, to, onClick, className }: IconButtonProps) {
  const cls = cx('icon-btn', className);
  if (to) {
    return (
      <Link className={cls} to={to} aria-label={label}>
        <Icon name={icon} />
      </Link>
    );
  }
  return (
    <button className={cls} type="button" aria-label={label} onClick={onClick}>
      <Icon name={icon} />
    </button>
  );
}

/** A row of buttons that share the width. */
export function Actions({ children }: { children: ReactNode }) {
  return <div className="actions">{children}</div>;
}
